import { getAuthManager, SCHEDULED_RENEW_LEAD_SECONDS } from './auth-manager'

export { SCHEDULED_RENEW_LEAD_SECONDS }

/**
 * How close to expiry an on-demand check (tab refocus, startup) has to be
 * before it renews.
 *
 * Matches the scheduled lead rather than sitting below it. An on-demand check
 * that declines to renew a token the scheduler has already given up on is the
 * gap that leaves a tab running on a dead token; and since every path now
 * funnels through the single flight below, an overlap between the two costs
 * nothing but a joined promise.
 */
export const ON_DEMAND_RENEW_THRESHOLD_SECONDS = SCHEDULED_RENEW_LEAD_SECONDS

/** True when `expiresAt` (epoch seconds) is inside the on-demand window. */
export function needsRenewal(expiresAt: number | undefined): boolean {
  if (expiresAt === undefined) return false
  const secondsLeft = expiresAt - Math.floor(Date.now() / 1000)
  return secondsLeft <= ON_DEMAND_RENEW_THRESHOLD_SECONDS
}

/**
 * Held briefly past settlement: clearing the guard in `.finally()` releases it
 * BEFORE downstream `.then()` handlers settle, and a caller landing in that gap
 * starts a second rotation of a refresh token the first call already replaced.
 */
const RENEWAL_REUSE_MS = 5000

/**
 * The renewal currently in flight, if any. Module-scoped on purpose: there is
 * one access token for the whole app, but several callers race to renew it —
 * every `useClientApi` instance that takes a 401, plus the expiring event,
 * startup recovery and the refocus check in the session renewal bridge.
 */
let inFlight: Promise<string> | null = null

/**
 * Single-flight renewal. Resolves with a usable access token or rejects with
 * the underlying error — pass that to `isDeadSessionError` to decide whether
 * the session is actually over.
 *
 * IDS6 issues rotating (one-time-use) refresh tokens, so two concurrent
 * renewals mean the loser gets `400 invalid_grant`; Duende treats the reused
 * token as replay and revokes the whole family. Funnelling every caller through
 * one in-flight promise collapses a burst into a single token request.
 */
export function renewSession(): Promise<string> {
  inFlight ??= getAuthManager()
    .signinSilent()
    .then((user) => {
      const token = user?.access_token
      if (!token) throw new Error('Silent renewal returned no access token')
      // Keep serving this token to late callers instead of rotating again.
      setTimeout(() => {
        inFlight = null
      }, RENEWAL_REUSE_MS)
      return token
    })
    .catch((error: unknown) => {
      // A failure is not reusable: drop it at once so the next caller can retry.
      inFlight = null
      throw error
    })

  return inFlight
}
